import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

// Fallback products in case API fails
const FALLBACK_PRODUCTS = [
  {
    id: "oridzin-classic",
    name_sr: "ORIGIN Classic",
    name_en: "ORIGIN Classic",
    description_sr: "Klasičan suvi džin sa bogatom aromom borovice",
    description_en: "Classic dry gin with rich juniper aroma",
    price: 4200,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/9mrnwy6e_Dzin-Classic-new.webp",
    aroma_sr: "Intenzivna borovica sa suptilnim tonovima korijandra i citrusa",
    aroma_en: "Intense juniper with subtle notes of coriander and citrus",
    taste_sr: "Suv, čist, sa bogatim biljnim karakterom i dugim završetkom",
    taste_en: "Dry, clean, with rich herbal character and long finish",
    serving_sr: "Sa premium tonikom, kriškom limuna i zrnom borovice",
    serving_en: "With premium tonic, lemon slice and juniper berry"
  },
  {
    id: "oridzin-citrus",
    name_sr: "ORIGIN Citrus",
    name_en: "ORIGIN Citrus",
    description_sr: "Osvežavajući džin sa naglašenim citrus notama",
    description_en: "Refreshing gin with pronounced citrus notes",
    price: 4200,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/2x2s1ysx_Dzin-Citrus-new.webp",
    aroma_sr: "Sveže note limuna i pomorandže sa blagim cvetnim akcentima",
    aroma_en: "Fresh lemon and orange notes with mild floral accents",
    taste_sr: "Osvežavajuć, citrusi dominiraju, sa glatkim završetkom",
    taste_en: "Refreshing, citrus-dominant, with smooth finish",
    serving_sr: "Sa tonikom, kriškom pomorandže i grančicom ruzmarina",
    serving_en: "With tonic, orange slice and rosemary sprig"
  },
  {
    id: "oridzin-uv",
    name_sr: "ORIGIN UV",
    name_en: "ORIGIN UV",
    description_sr: "Jedinstven džin koji menja boju - od plave do ljubičaste",
    description_en: "Unique color-changing gin - from blue to purple",
    price: 4600,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/7nbi26rk_Dzin-UV-new.webp",
    aroma_sr: "Cvetne note lavande i borovice sa egzotičnim akcentima",
    aroma_en: "Floral notes of lavender and juniper with exotic accents",
    taste_sr: "Elegantan, cvetni, sa misterioznim završetkom",
    taste_en: "Elegant, floral, with mysterious finish",
    serving_sr: "Sa tonikom i kriškom limuna - posmatrajte magičnu promenu boje!",
    serving_en: "With tonic and lemon slice - watch the magical color change!"
  }
];

export default function Shop() {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/products.php')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setProducts(data);
        } else {
          setProducts(FALLBACK_PRODUCTS);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        setProducts(FALLBACK_PRODUCTS);
        setLoading(false);
      });
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sr-RS').format(price);
  };

  const getProductName = (product) => language === 'sr' ? product.name_sr : product.name_en;
  const getShortDesc = (product) => language === 'sr' ? product.description_sr : product.description_en;

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#0A0A0A]" data-testid="shop-page">
      <SEO 
        title={language === 'sr' ? 'Prodavnica' : 'Shop'}
        description={language === 'sr' 
          ? 'Kupite ОРИЏИН džinove online - Classic, Citrus i UV. Besplatna dostava za porudžbine preko 5000 RSD. Poklon pakovanja dostupna.'
          : 'Buy ORIGIN gins online - Classic, Citrus and UV. Free delivery for orders over 5000 RSD. Gift packaging available.'
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="decorative-line mx-auto mb-8" />
          <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
            {t('products_subtitle')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
            {t('products_title')}
          </h1>
          <p className="text-white/40 max-w-2xl mx-auto">
            {language === 'sr'
              ? 'Svaki naš džin je ručno pravljen u malim serijama sa pažljivo odabranim botaničkim sastojcima. Otkrijte jedinstvene ukuse naše ОРИЏИН kolekcije.'
              : 'Each of our gins is handcrafted in small batches with carefully selected botanical ingredients. Discover the unique flavors of our ORIGIN collection.'}
          </p>
        </div>

        {/* Products */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-24">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
                data-testid={`product-card-${product.id}`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <Link to={`/product/${product.id}`}>
                    <div className="aspect-[3/4] flex items-center justify-center bg-gradient-to-b from-transparent via-white/5 to-transparent p-8 overflow-hidden group">
                      <img
                        src={product.image_url}
                        alt={getProductName(product)}
                        className="max-h-full w-auto object-contain transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  {product.id === 'oridzin-uv' && (
                    <div className="absolute inset-0 bg-purple-500/10 blur-[80px] -z-10" />
                  )}
                </div>

                {/* Info */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="decorative-line mb-8" />
                  <Link to={`/product/${product.id}`}>
                    <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white hover:text-gold transition-colors mb-4">
                      {getProductName(product)}
                    </h2>
                  </Link>
                  
                  <div className="flex items-center gap-6 mb-6">
                    <span className="text-gold text-2xl font-heading">
                      {formatPrice(product.price)} <span className="text-sm">RSD</span>
                    </span>
                    <span className="text-white/30 text-sm">{product.abv}% vol.</span>
                  </div>
                  
                  <p className="text-white/50 leading-relaxed mb-6">
                    {getShortDesc(product)}
                  </p>
                  
                  {/* Tasting notes */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-surface/50 p-4 border border-white/5">
                      <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('aroma')}</p>
                      <p className="text-white/50 text-sm line-clamp-3">
                        {language === 'sr' ? product.aroma_sr : product.aroma_en}
                      </p>
                    </div>
                    <div className="bg-surface/50 p-4 border border-white/5">
                      <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('taste')}</p>
                      <p className="text-white/50 text-sm line-clamp-3">
                        {language === 'sr' ? product.taste_sr : product.taste_en}
                      </p>
                    </div>
                    <div className="bg-surface/50 p-4 border border-white/5">
                      <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('serving')}</p>
                      <p className="text-white/50 text-sm line-clamp-3">
                        {language === 'sr' ? product.serving_sr : product.serving_en}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => addItem(product)}
                      className="bg-gold hover:bg-gold-light text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {t('add_to_cart')}
                    </button>
                    <Link
                      to={`/product/${product.id}`}
                      className="border border-gold/50 text-gold hover:bg-gold/10 px-8 py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                    >
                      {language === 'sr' ? 'Saznaj više' : 'Learn more'}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
